import type { ReactNode } from "react";
import { Link } from "react-router";

/**
 * Sobre, Política de Privacidade e Termos de Uso, públicas (sem login). A verificação
 * do app Google (migration 302) exige as duas no domínio da plataforma, com o
 * aviso de "Uso Limitado" dos dados das APIs do Google.
 *
 * Texto escrito em 29/10/2026 (razão social e CNPJ em 30/10; IA e Uso Limitado em
 * inglês em 04/11, pedido da verificação do Google); falta revisão
 * jurídica.
 */

const CONTATO = "contato@northbusinessteam.com";
const EMPRESA = "NORTH BUSINESS TEAM CORRETORA DE SEGUROS LTDA";
const CNPJ = "51.245.622/0001-10";
const VIGENCIA = "4 de novembro de 2026";

const Pagina = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <main className="min-h-dvh bg-background px-4 py-10 text-foreground">
    <article className="mx-auto max-w-3xl space-y-6 text-[15px] leading-relaxed [&_h2]:pt-2 [&_h2]:text-lg [&_h2]:font-semibold [&_li]:ml-5 [&_li]:list-disc">
      <header className="space-y-1 border-b pb-4">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Plataforma North</p>
        <h1 className="text-2xl font-bold">{titulo}</h1>
        <p className="text-sm text-muted-foreground">Vigente desde {VIGENCIA}.</p>
      </header>
      {children}
      <footer className="space-y-2 border-t pt-4 text-sm text-muted-foreground">
        <p>
          {EMPRESA} · CNPJ {CNPJ} · <a href={`mailto:${CONTATO}`} className="underline">{CONTATO}</a>
        </p>
        <p className="flex gap-4">
          <Link to="/sobre" className="underline">Sobre</Link>
          <Link to="/privacidade" className="underline">Política de Privacidade</Link>
          <Link to="/termos" className="underline">Termos de Uso</Link>
        </p>
      </footer>
    </article>
  </main>
);

/** Página inicial pública do app Google (verificação exige explicar o app sem login). */
export const SobrePage = () => (
  <Pagina titulo="Plataforma North">
    <p>
      Sistema de gestão comercial da {EMPRESA} para consórcio e seguros, usado por vendedores, gestores e
      escritórios parceiros.
    </p>

    <h2>Funções</h2>
    <ul>
      <li>Cadastro e acompanhamento de clientes, oportunidades e tarefas.</li>
      <li>Simulação de consórcio, estudo de lances e propostas em PDF.</li>
      <li>Cotação e acompanhamento de seguros de vida junto às seguradoras parceiras.</li>
      <li>Controle de vendas, comissões, repasses e fluxo de caixa.</li>
      <li>Assistente North IA para consultas sobre a carteira.</li>
    </ul>

    <h2>Uso da conta Google</h2>
    <p>
      A conexão com a conta Google é opcional e feita pelo próprio usuário em Meu perfil › Conexões. Com ela, a
      plataforma:
    </p>
    <ul>
      <li>envia pelo Gmail do usuário os e-mails que ele escreve e confirma para os próprios clientes (gmail.send);</li>
      <li>
        cria na Agenda Google do usuário as reuniões com clientes, com convite e link do Google Meet, e os
        lembretes das tarefas registradas na plataforma (calendar.events).
      </li>
    </ul>
    <p>
      A plataforma não lê a caixa de entrada nem os eventos existentes. Detalhes na{" "}
      <Link to="/privacidade" className="underline">Política de Privacidade</Link>.
    </p>

    <h2>Acesso</h2>
    <p>
      Acesso restrito a usuários cadastrados pela North ou pelo escritório parceiro.{" "}
      <Link to="/login" className="underline">Entrar na plataforma</Link>.
    </p>
  </Pagina>
);
SobrePage.path = "/sobre";

export const PoliticaPrivacidadePage = () => (
  <Pagina titulo="Política de Privacidade">
    <p>
      Esta política descreve como a {EMPRESA} (CNPJ {CNPJ}), controladora dos dados, trata dados pessoais na
      Plataforma North, sistema de
      gestão comercial para consórcio e seguros usado por vendedores, gestores e escritórios parceiros. O
      tratamento segue a Lei Geral de Proteção de Dados (Lei 13.709/2018).
    </p>

    <h2>Dados tratados</h2>
    <ul>
      <li>Dados de cadastro dos usuários: nome, e-mail, celular, CPF ou CNPJ e dados de acesso.</li>
      <li>
        Dados de clientes e contatos registrados pelos usuários: identificação, contato, informações financeiras e
        de saúde informadas para cotação de seguros, propostas, vendas e histórico de atendimento.
      </li>
      <li>Registros de uso: acessos, ações realizadas e informações técnicas do dispositivo.</li>
    </ul>

    <h2>Finalidades</h2>
    <p>
      Os dados são usados para operar a plataforma: registrar e acompanhar clientes, simular e emitir propostas,
      enviar propostas às administradoras e seguradoras, calcular comissões, gerar relatórios e prestar suporte.
      As bases legais são a execução de contrato, o cumprimento de obrigação legal e o legítimo interesse, e o
      consentimento quando exigido.
    </p>

    <h2>Integração com a conta Google</h2>
    <p>
      A conexão com a conta Google é opcional e feita pelo próprio usuário em Meu perfil › Conexões. Com ela, a
      plataforma pode:
    </p>
    <ul>
      <li>enviar e-mails pelo Gmail do usuário, quando ele escreve e confirma o envio (permissão gmail.send);</li>
      <li>
        criar, alterar e remover eventos na Agenda Google do usuário a partir das tarefas e reuniões que ele
        registra (permissão calendar.events);
      </li>
      <li>identificar o endereço de e-mail da conta conectada (permissões openid e email).</li>
    </ul>
    <p>
      A plataforma não lê, não armazena e não analisa o conteúdo da caixa de entrada nem os eventos já existentes
      na agenda. O token de acesso fica criptografado no cofre do banco de dados e é usado somente para as ações
      acima, no momento em que o usuário as executa. A conexão pode ser removida a qualquer momento em Meu perfil
      › Conexões ou nas configurações de segurança da conta Google.
    </p>
    <p>
      O uso e a transferência para qualquer outro aplicativo de informações recebidas das APIs do Google pela
      Plataforma North seguem a Política de Dados do Usuário dos Serviços de API do Google, incluindo os requisitos
      de Uso Limitado. Esses dados não são vendidos, não são usados para publicidade e não são usados para treinar
      modelos de inteligência artificial.
    </p>
    <p>
      O assistente North IA usa a API Gemini do Google no plano pago, em que as solicitações e as respostas não são
      usadas pelo Google para treinar ou melhorar modelos. O assistente redige e-mails e reuniões a partir dos dados
      do CRM e só os envia depois da confirmação do usuário. Nenhuma informação recebida das APIs do Google
      Workspace, como o endereço da conta conectada, identificadores ou links de eventos, é enviada ao assistente ou
      a outro serviço de inteligência artificial.
    </p>
    <p lang="en">
      Limited Use: the use of raw or derived user data received from Google Workspace APIs will adhere to the Google
      User Data Policy, including the Limited Use requirements. Google user data is not transferred to any AI
      service and is not used to develop, improve or train generalized or foundational AI/ML models.
    </p>

    <h2>Compartilhamento</h2>
    <p>
      Os dados são compartilhados somente quando necessário à operação: com administradoras de consórcio e
      seguradoras, para cotação, proposta e acompanhamento de contratos; e com provedores de infraestrutura e de
      envio de mensagens contratados pela North, sob obrigação de confidencialidade. Autoridades recebem dados
      apenas por determinação legal.
    </p>

    <h2>Armazenamento e segurança</h2>
    <p>
      Os dados ficam em servidores de nuvem com acesso restrito por perfil de usuário, conexão criptografada e
      senhas de terceiros guardadas em cofre criptografado. Cada usuário vê apenas os dados que o perfil dele
      permite.
    </p>

    <h2>Retenção</h2>
    <p>
      Os dados são mantidos enquanto a conta estiver ativa e pelo prazo exigido por obrigações legais e
      regulatórias. Depois disso, são excluídos ou anonimizados.
    </p>

    <h2>Direitos do titular</h2>
    <p>
      O titular pode pedir confirmação de tratamento, acesso, correção, anonimização, portabilidade, exclusão e
      informações sobre compartilhamento, além de revogar consentimentos, pelo e-mail{" "}
      <a href={`mailto:${CONTATO}`} className="underline">{CONTATO}</a>.
    </p>

    <h2>Alterações</h2>
    <p>Alterações nesta política são publicadas nesta página, com a nova data de vigência.</p>
  </Pagina>
);
PoliticaPrivacidadePage.path = "/privacidade";

export const TermosDeUsoPage = () => (
  <Pagina titulo="Termos de Uso">
    <p>
      Estes termos regem o uso da Plataforma North, oferecida pela {EMPRESA} (CNPJ {CNPJ}) a vendedores, gestores e
      escritórios parceiros. O acesso à plataforma implica a aceitação destes termos e da{" "}
      <Link to="/privacidade" className="underline">Política de Privacidade</Link>.
    </p>

    <h2>Acesso</h2>
    <p>
      O acesso é pessoal e intransferível, liberado pela North ou pelo escritório responsável pelo usuário. Cada
      login permite uma sessão ativa por vez. O usuário responde pela guarda da própria senha e pelas ações feitas
      com o seu acesso.
    </p>

    <h2>Uso permitido</h2>
    <ul>
      <li>Registrar apenas dados de clientes obtidos de forma lícita e necessários ao atendimento.</li>
      <li>Não usar a plataforma para envio de mensagens não solicitadas em massa.</li>
      <li>Não tentar acessar dados de outros usuários ou contornar as restrições de perfil.</li>
    </ul>

    <h2>Conexões com serviços de terceiros</h2>
    <p>
      As conexões com portais de administradoras, seguradoras, WhatsApp e conta Google são opcionais e feitas pelo
      próprio usuário. As ações executadas por elas, como envio de e-mail, eventos de agenda e operações nos
      portais, ocorrem em nome do usuário e mediante a confirmação dele. O uso desses serviços também segue os
      termos de cada fornecedor.
    </p>

    <h2>Simulações e sugestões</h2>
    <p>
      Simulações, projeções e sugestões de lance ou de capital são estimativas baseadas nos dados disponíveis e não
      garantem contemplação, aprovação de proposta ou resultado financeiro.
    </p>

    <h2>Disponibilidade</h2>
    <p>
      A North mantém a plataforma em funcionamento contínuo, mas pode suspender o acesso para manutenção ou por
      falha de serviços de terceiros, sem que isso gere indenização.
    </p>

    <h2>Encerramento</h2>
    <p>
      O acesso pode ser encerrado pela North ou pelo escritório responsável, inclusive em caso de uso contrário a
      estes termos.
    </p>

    <h2>Contato</h2>
    <p>
      Dúvidas sobre estes termos: <a href={`mailto:${CONTATO}`} className="underline">{CONTATO}</a>.
    </p>
  </Pagina>
);
TermosDeUsoPage.path = "/termos";
